import React, { useState, useEffect } from 'react';
import { 
  Leaf, Microscope, BookOpen, TrendingUp, Filter, Search, Eye, Clock, 
  MessageCircle, Heart, Bookmark, Share2, ChevronDown, ChevronRight,
  Award, Users, Calendar, Tag, ExternalLink, Download, Play,
  Beaker, Sprout, FileText, Globe, Star, ThumbsUp, ArrowRight,
  Video, PieChart, BarChart3, Activity, Bell, Sparkles, Trophy,
  Flame, Zap, X, Plus, ChevronUp, Target, Crown, Coffee,
  ArrowLeft, Reply, MoreHorizontal, ThumbsDown, Link, Flag, ArrowUp, ArrowDown
} from 'lucide-react';

export const RelatedArticlesSection = ({ colors, article, handleOpenArticle, relatedArticles = [], loadingRelated = false }) => {

return(
  <div style={{
    background: colors.bgTertiary,
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid #333',
    marginBottom: '24px'
  }}>
    <h3 style={{ 
      margin: '0 0 16px 0', 
      color: '#e5e5e5', 
      fontSize: '16px', 
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }}>
      <BookOpen size={16} />
      Related Articles
      {relatedArticles.length > 0 && (
        <span style={{
          background: '#00d4aa20',
          color: '#00d4aa',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          {relatedArticles.length}
        </span>
      )}
    </h3>

    {loadingRelated ? (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        color: '#888'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          border: '2px solid #333',
          borderTop: '2px solid #00d4aa',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginRight: '12px'
        }} />
        Loading related articles...
      </div>
    ) : relatedArticles.length === 0 ? (
      <div style={{
        textAlign: 'center',
        padding: '40px 20px',
        color: '#666',
        fontSize: '14px'
      }}>
        <BookOpen size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
        <p style={{ margin: 0 }}>No related articles found</p>
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {relatedArticles.map((relatedArticle) => (
          <div 
            key={relatedArticle.id}
            style={{
              display: 'flex',
              gap: '12px',
              padding: '12px',
              background: colors.bgCard,
              borderRadius: '12px',
              border: '1px solid #333',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative'
            }}
            onClick={() => handleOpenArticle(relatedArticle)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#00d4aa';
              e.currentTarget.style.transform = 'translateX(4px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 212, 170, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#333';
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {/* Article thumbnail or category icon */}
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '8px',
              background: relatedArticle.thumbnail 
                ? `url(${relatedArticle.thumbnail})`
                : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {!relatedArticle.thumbnail && (
                <Microscope size={24} style={{ color: 'white' }} />
              )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Similarity score indicator */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '4px'
              }}>
                <div style={{
                  padding: '2px 6px',
                  background: relatedArticle.similarity_score > 0.7 ? '#10b981' : 
                            relatedArticle.similarity_score > 0.4 ? '#fbbf24' : '#6b7280',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  {Math.round(relatedArticle.similarity_score * 100)}% match
                </div>
                
                {relatedArticle.category === article.category && (
                  <div style={{
                    padding: '2px 6px',
                    background: '#8b5cf620',
                    color: '#8b5cf6',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    Same category
                  </div>
                )}

                {relatedArticle.is_trending && (
                  <TrendingUp size={12} style={{ color: '#8b5cf6' }} />
                )}
                {relatedArticle.is_hot && (
                  <Flame size={12} style={{ color: '#ef4444' }} />
                )}
              </div>

              <h4 style={{
                margin: '0 0 6px 0',
                fontSize: '13px',
                fontWeight: '600',
                color: '#e5e5e5',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {relatedArticle.title}
              </h4>

              {/* Matching tags */}
              {relatedArticle.matching_tags && relatedArticle.matching_tags.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  marginBottom: '6px'
                }}>
                  {relatedArticle.matching_tags.slice(0, 3).map((tag) => (
                    <span 
                      key={tag}
                      style={{
                        padding: '2px 6px',
                        background: '#00d4aa20',
                        color: '#00d4aa',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '500'
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                  {relatedArticle.matching_tags.length > 3 && (
                    <span style={{
                      padding: '2px 6px',
                      background: '#333',
                      color: '#888',
                      borderRadius: '4px',
                      fontSize: '10px'
                    }}>
                      +{relatedArticle.matching_tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                fontSize: '11px', 
                color: '#888' 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <Eye size={10} />
                  {relatedArticle.view_count || 0}
                </div>
                <span>•</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <Heart size={10} />
                  {relatedArticle.like_count || 0}
                </div>
                <span>•</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <Calendar size={10} />
                  {relatedArticle.published_date}
                </div>
              </div>
            </div>

            {/* Quick action button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(relatedArticle.url, '_blank');
              }}
              style={{
                padding: '8px',
                background: 'transparent',
                border: '1px solid #444',
                borderRadius: '6px',
                color: '#888',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                alignSelf: 'flex-start'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#00d4aa';
                e.currentTarget.style.color = '#00d4aa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#444';
                e.currentTarget.style.color = '#888';
              }}
            >
              <ExternalLink size={12} />
            </button>
          </div>
        ))}
      </div>
    )}

    {relatedArticles.length > 0 && (
      <div style={{
        marginTop: '16px',
        padding: '12px',
        background: colors.bgCard,
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <p style={{
          margin: '0 0 8px 0',
          fontSize: '12px',
          color: '#888'
        }}>
          Found {relatedArticles.length} related articles based on shared tags
        </p>
        <button style={{
          padding: '6px 12px',
          background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600',
          cursor: 'pointer'
        }}>
          View All Related
        </button>
      </div>
    )}
  </div>
)};
